import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Avatar, IconButton, Fab, Dialog, DialogTitle, DialogContent,
  CircularProgress, Chip, Collapse, Card, CardContent, CardActions, Divider,
  Skeleton, SwipeableDrawer, Button, TextField, InputAdornment, Tabs, Tab, Slider
} from '@mui/material';
import {
  Add as AddIcon, Edit, Delete, ExpandMore, ExpandLess,
  TrendingUp, TrendingDown, AccountBalance, Refresh, Star, Calculate
} from '@mui/icons-material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { useIntl, FormattedMessage } from 'react-intl';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'firebase/auth';
import { Investment } from '../types/Investment';
import { getInvestments, deleteInvestment, addInvestment, updateInvestment } from '../firebase/firestoreOperations';
import { getMultipleCryptoPrices, getTokenDetails, TokenDetails } from '../services/coingeckoService';
import InvestmentForm from './InvestmentForm';
import { auth } from '../firebase/config';
import { designTokens } from '../theme/designTokens';

interface PriceData {
  price: number;
  price_change_percentage_24h: number;
}

interface MobileInvestmentsProps {
  user: User;
}

const MobileInvestments: React.FC<MobileInvestmentsProps> = ({ user }) => {
  const intl = useIntl();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [currentPrices, setCurrentPrices] = useState<{ [key: string]: PriceData }>({});
  const [tokenDetails, setTokenDetails] = useState<{ [key: string]: TokenDetails }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | undefined>(undefined);
  const [expandedTokens, setExpandedTokens] = useState<Set<string>>(new Set());
  const [forecastPrices, setForecastPrices] = useState<{ [key: string]: string }>({});
  const [simulationAmounts, setSimulationAmounts] = useState<{ [key: string]: string }>({});
  const [activeAnalysisTab, setActiveAnalysisTab] = useState(0);

  // --- Take Profits States ---
  const [tpStrategy, setTpStrategy] = useState<{ [key: string]: 'simple' | 'riskFree' | 'dca' }>({});
  const [tpSimpleTargetPrice, setTpSimpleTargetPrice] = useState<{ [key: string]: string }>({});
  const [tpSimpleSellPercentage, setTpSimpleSellPercentage] = useState<{ [key: string]: number }>({});
  const [tpRiskFreeSellPercentage, setTpRiskFreeSellPercentage] = useState<{ [key: string]: number }>({});
  const [tpDcaSteps, setTpDcaSteps] = useState<{ [key: string]: { price: string, percentage: number }[] }>({});

  const getTokenId = (token: string): string => {
    switch (token.toLowerCase()) {
      case 'ada': return 'cardano';
      case 'eth': return 'ethereum';
      case 'btc': return 'bitcoin';
      case 'link': return 'chainlink';
      case 'night': return 'midnight-3';
      default: return token.toLowerCase();
    }
  };

  const fetchInvestments = useCallback(async () => {
    if (!user) return;
    try {
      const fetchedInvestments = await getInvestments(user.uid);
      const cryptoInvestments = fetchedInvestments.filter(inv => inv.type !== 'etf');
      setInvestments(cryptoInvestments);

      const tokens = cryptoInvestments
        .map(inv => inv.token)
        .filter(token => token && typeof token === 'string' && token.trim() !== '');

      if (tokens.length === 0) return;

      try {
        const prices = await getMultipleCryptoPrices(tokens);
        setCurrentPrices(prices as { [key: string]: PriceData });

        const details = await Promise.all(tokens.map(token => getTokenDetails(token)));
        const detailsMap = details.reduce((acc: { [key: string]: TokenDetails }, detail: TokenDetails | null) => {
          if (detail) acc[detail.symbol] = detail;
          return acc;
        }, {});
        setTokenDetails(detailsMap);
      } catch (error) {
        console.error('Error fetching prices/details:', error);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout;

    const loadData = async () => {
      setIsLoading(true);
      if (user && isMounted) {
        await fetchInvestments();
        setIsLoading(false);
        intervalId = setInterval(fetchInvestments, 60000);
      }
    };

    loadData();
    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, fetchInvestments]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchInvestments();
    setIsRefreshing(false);
  };

  // --- Calculations ---
  const portfolioSummary = useMemo(() => {
    let totalInvestment = 0;
    let currentValue = 0;

    investments.forEach(investment => {
      if (!investment.token) return;
      const priceData = currentPrices[investment.token.toUpperCase()];
      totalInvestment += parseFloat(investment.investment);
      if (priceData) {
        currentValue += priceData.price * Number(investment.quantity);
      }
    });

    const profitLoss = currentValue - totalInvestment;
    const profitLossPercentage = totalInvestment > 0 ? (profitLoss / totalInvestment) * 100 : 0;

    return { totalInvestment, currentValue, profitLoss, profitLossPercentage };
  }, [investments, currentPrices]);

  const tokenSummaries = useMemo(() => {
    const totals: { [key: string]: { quantity: number; investment: number } } = {};
    investments.forEach(inv => {
      if (!totals[inv.token]) totals[inv.token] = { quantity: 0, investment: 0 };
      totals[inv.token].quantity += Number(inv.quantity);
      totals[inv.token].investment += parseFloat(inv.investment);
    });
    return totals;
  }, [investments]);

  const longTermTokens = useMemo(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const tokens = new Set<string>();
    investments.forEach(inv => {
      if (inv.purchaseDate && new Date(inv.purchaseDate) < oneYearAgo) {
        tokens.add(inv.token);
      }
    });
    return tokens;
  }, [investments]);

  // --- Formatting ---
  const formatPrice = (token: string, priceData: PriceData | undefined) => {
    if (!priceData || priceData.price === undefined) return 'N/A';
    if (!token) return 'N/A';
    if (token.toUpperCase() === 'ADA') return `€${priceData.price.toFixed(4)}`;
    if (token.toUpperCase() === 'NIGHT') return `€${priceData.price.toFixed(3)}`;
    return `€${priceData.price.toFixed(2)}`;
  };

  const formatQuantity = (token: string, quantity: number) => {
    if (quantity === undefined || quantity === null || isNaN(quantity)) return '0.00';
    if (!token) return Number(quantity).toFixed(2);
    if (token.toUpperCase() === 'BTC') return Number(quantity).toFixed(4);
    return Number(quantity).toFixed(2);
  };

  const formatPurchasePrice = (token: string, price: number) => {
    if (price === undefined || price === null || isNaN(price)) return '0.00';
    if (!token) return Number(price).toFixed(2);
    if (token.toUpperCase() === 'ADA') return Number(price).toFixed(4);
    if (token.toUpperCase() === 'NIGHT') return Number(price).toFixed(3);
    return Number(price).toFixed(2);
  };

  // --- Handlers ---
  const toggleToken = (token: string) => {
    setExpandedTokens(prev => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  const handleEdit = (investment: Investment) => {
    setEditingInvestment(investment);
    setIsEditFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(intl.formatMessage({ id: 'confirmDeleteInvestment' }))) {
      try {
        await deleteInvestment(id, user.uid);
        await fetchInvestments();
      } catch (error) {
        console.error('Error deleting investment:', error);
      }
    }
  };

  const handleAddInvestment = async (investment: Omit<Investment, 'id'>) => {
    if (!auth.currentUser) return;
    try {
      const tokenId = getTokenId(investment.token);
      const [tokenDetail, priceData] = await Promise.all([
        getTokenDetails(tokenId),
        getMultipleCryptoPrices([tokenId])
      ]);
      if (tokenDetail) setTokenDetails(prev => ({ ...prev, [tokenId]: tokenDetail }));
      if (priceData && priceData[tokenId]) setCurrentPrices(prev => ({ ...prev, [tokenId]: priceData[tokenId] as PriceData }));

      await addInvestment(auth.currentUser.uid, investment);
      await fetchInvestments();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error adding investment:', error);
    }
  };

  const handleUpdateInvestment = async (id: string, investment: Partial<Investment>) => {
    try {
      await updateInvestment(id, investment, user.uid);
      await fetchInvestments();
      setEditingInvestment(undefined);
      setIsEditFormOpen(false);
    } catch (error) {
      console.error('Error updating investment:', error);
    }
  };

  const handleForecastPriceChange = (token: string, value: string) => {
    let price = value.replace(',', '.');
    if (token.toUpperCase() === 'ADA') {
      price = price.match(/^\d*\.?\d{0,4}$/)?.[0] || '';
    } else if (token.toUpperCase() === 'NIGHT') {
      price = price.match(/^\d*\.?\d{0,3}$/)?.[0] || '';
    } else {
      price = price.match(/^\d*\.?\d{0,2}$/)?.[0] || '';
    }
    setForecastPrices(prev => ({ ...prev, [token]: price }));
  };

  const handleSimulationAmountChange = (token: string, value: string) => {
    let amount = value.replace(',', '.');
    amount = amount.match(/^\d*\.?\d{0,2}$/)?.[0] || '';
    setSimulationAmounts(prev => ({ ...prev, [token]: amount }));
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: designTokens.borderRadius.lg }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 2, overflow: 'hidden' }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" width={100} height={40} sx={{ borderRadius: designTokens.borderRadius.full, flexShrink: 0 }} />
          ))}
        </Box>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1.5, borderRadius: designTokens.borderRadius.md }} />
        ))}
      </Box>
    );
  }

  // --- Simulation Cards ---
  const renderTaxExemptCard = () => {
    const longTermHoldings: { [key: string]: { quantity: number; totalInvestment: number; currentValue: number } } = {};
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    investments.forEach((investment) => {
      if (investment.purchaseDate && new Date(investment.purchaseDate) < oneYearAgo) {
        if (!longTermHoldings[investment.token]) {
          longTermHoldings[investment.token] = { quantity: 0, totalInvestment: 0, currentValue: 0 };
        }
        longTermHoldings[investment.token].quantity += Number(investment.quantity);
        longTermHoldings[investment.token].totalInvestment += parseFloat(investment.investment);

        const priceData = currentPrices[investment.token.toUpperCase()];
        const currentPrice = priceData ? priceData.price : 0;
        longTermHoldings[investment.token].currentValue += currentPrice * Number(investment.quantity);
      }
    });

    if (Object.keys(longTermHoldings).length === 0) return null;

    const summary = Object.values(longTermHoldings).reduce((acc, curr) => ({
      currentValue: acc.currentValue + curr.currentValue,
      totalInvestment: acc.totalInvestment + curr.totalInvestment
    }), { currentValue: 0, totalInvestment: 0 });

    const totalProfitLoss = summary.currentValue - summary.totalInvestment;
    const totalProfitLossPercentage = summary.totalInvestment > 0 ? (totalProfitLoss / summary.totalInvestment) * 100 : 0;

    return (
      <Card sx={{
        mb: 2, borderRadius: designTokens.borderRadius.lg,
        border: `1px solid rgba(52, 211, 153, 0.4)`,
        boxShadow: designTokens.shadows.sm, overflow: 'hidden',
        background: designTokens.colors.background.paper
      }}>
        {/* Sticky Header with Totals */}
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.15), rgba(16, 185, 129, 0.05))',
          borderBottom: '1px solid rgba(52, 211, 153, 0.3)',
          px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1
        }}>
          <Typography variant="caption" sx={{ color: designTokens.colors.success.main, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
            ⭐ Tax Exempt Portfolio
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{Math.floor(summary.currentValue)}€</Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Tax-Free Profit</Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 700, 
                color: totalProfitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main 
              }}>
                {totalProfitLoss >= 0 ? '+' : ''}{Math.floor(totalProfitLoss)}€ ({totalProfitLossPercentage.toFixed(1)}%)
              </Typography>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: '0 !important' }}>
          {Object.entries(longTermHoldings).map(([token, data], idx) => {
            const details = token ? tokenDetails[token.toUpperCase()] : undefined;
            const profitLoss = data.currentValue - data.totalInvestment;
            const profitLossPercentage = data.totalInvestment > 0 ? (profitLoss / data.totalInvestment) * 100 : 0;
            return (
              <Box key={token} sx={{ p: 2, borderBottom: idx < Object.keys(longTermHoldings).length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {details?.image ? (
                    <Avatar src={details.image} sx={{ width: 32, height: 32, mr: 1.5 }} />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem' }}>{token?.charAt(0)}</Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{token?.toUpperCase()}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {formatQuantity(token, data.quantity)} tokens
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{Math.floor(data.currentValue)}€</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: profitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main }}>
                      {profitLoss >= 0 ? <ArrowDropUp sx={{ fontSize: '1rem' }} /> : <ArrowDropDown sx={{ fontSize: '1rem' }} />}
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {profitLoss >= 0 ? '+' : ''}{Math.floor(profitLoss)}€ ({profitLossPercentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderForecastSimulationCard = () => {
    if (Object.keys(tokenSummaries).length === 0) return null;

    let totalForecastValue = 0;
    let totalPotentialProfit = 0;
    let totalInvestment = 0;

    const tokensList = Object.entries(tokenSummaries).map(([token, data]) => {
      const forecastPrice = parseFloat(forecastPrices[token] || '0');
      const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
      const forecastValue = Math.floor(data.quantity * forecastPrice);
      const potentialProfit = Math.floor(forecastValue - data.investment);
      const potentialProfitPercentage = data.investment > 0 ? (potentialProfit / data.investment) * 100 : 0;
      const growthVsCurrent = currentPrice > 0 ? ((forecastPrice - currentPrice) / currentPrice) * 100 : 0;
      const currentValue = currentPrice * data.quantity;
      
      totalForecastValue += forecastValue;
      totalPotentialProfit += potentialProfit;
      totalInvestment += data.investment;

      return { token, forecastPrice, forecastValue, potentialProfit, potentialProfitPercentage, growthVsCurrent, currentValue };
    });

    const totalCurrentValue = tokensList.reduce((acc, t) => acc + t.currentValue, 0);
    const totalGrowthVsCurrent = totalCurrentValue > 0 ? ((totalForecastValue - totalCurrentValue) / totalCurrentValue) * 100 : 0;
    const totalPotentialProfitPercentage = totalInvestment > 0 ? (totalPotentialProfit / totalInvestment) * 100 : 0;

    return (
      <Card sx={{
        mb: 2, borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        boxShadow: designTokens.shadows.sm, overflow: 'hidden'
      }}>
        {/* Sticky Header with Totals */}
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.05))',
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1
        }}>
          <Typography variant="caption" sx={{ color: designTokens.colors.info.main, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
            🎯 Simulated Portfolio
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{totalForecastValue}€</Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Potential Profit</Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 700, 
                color: totalPotentialProfit >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main 
              }}>
                {totalPotentialProfit >= 0 ? '+' : ''}{totalPotentialProfit}€ ({totalPotentialProfitPercentage.toFixed(1)}%)
              </Typography>
              <Typography variant="caption" sx={{ 
                fontWeight: 600, 
                fontSize: '0.65rem',
                opacity: 0.9,
                color: totalGrowthVsCurrent >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main 
              }}>
                {intl.formatMessage({ id: 'growthVsCurrent' })}: {totalGrowthVsCurrent >= 0 ? '+' : ''}{totalGrowthVsCurrent.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: '0 !important' }}>
          {tokensList.map(({ token, forecastValue, potentialProfit, potentialProfitPercentage, growthVsCurrent }, idx) => {
            const details = token ? tokenDetails[token.toUpperCase()] : undefined;
            return (
              <Box key={token} sx={{ p: 2, borderBottom: idx < tokensList.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  {details?.image ? (
                    <Avatar src={details.image} sx={{ width: 32, height: 32, mr: 1.5 }} />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem' }}>{token?.charAt(0)}</Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{token?.toUpperCase()}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Target Price</Typography>
                  </Box>
                  <Box sx={{ width: '45%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputMode="decimal"
                      placeholder="Price"
                      value={forecastPrices[token] || ''}
                      onChange={(e) => handleForecastPriceChange(token, e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 2, fontWeight: 700,
                          bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.9rem'
                        } 
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Result Pill */}
                {(forecastPrices[token] && parseFloat(forecastPrices[token]) > 0) ? (
                  <Box sx={{ 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    background: 'rgba(255,255,255,0.03)', borderRadius: 2, p: 1.5
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>Expected Value: {forecastValue}€</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 700,
                        display: 'block',
                        color: potentialProfit >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main 
                      }}>
                        {potentialProfit >= 0 ? '+' : ''}{potentialProfit}€ ({potentialProfitPercentage.toFixed(1)}%)
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        opacity: 0.9,
                        color: growthVsCurrent >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                      }}>
                        {intl.formatMessage({ id: 'growthVsCurrent' })}: {growthVsCurrent >= 0 ? '+' : ''}{growthVsCurrent.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderInvestmentSimulationCard = () => {
    if (Object.keys(tokenSummaries).length === 0) return null;

    return (
      <Card sx={{
        mb: 2, borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        boxShadow: designTokens.shadows.sm, overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(147, 51, 234, 0.05))',
          borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
          px: 2, py: 2
        }}>
          <Typography variant="caption" sx={{ color: designTokens.colors.chart.purple, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
            💡 Investment Calculator
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, lineHeight: 1.3 }}>
            Simulate how investing heavily at current prices impacts your average purchase price.
          </Typography>
        </Box>

        <CardContent sx={{ p: '0 !important' }}>
          {Object.entries(tokenSummaries).map(([token, data], idx) => {
            const amount = parseFloat(simulationAmounts[token] || '0');
            const currentPrice = token ? (currentPrices[token.toUpperCase()]?.price || 0) : 0;
            const details = token ? tokenDetails[token.toUpperCase()] : undefined;

            let tokensReceived = 0;
            let newAvgPrice = 0;
            let oldAvgPrice = data.quantity > 0 ? (data.investment / data.quantity) : 0;
            let percentageChange = 0;

            if (amount > 0 && currentPrice > 0) {
              tokensReceived = amount / currentPrice;
              const newTotalQuantity = data.quantity + tokensReceived;
              const newTotalInvestment = data.investment + amount;
              newAvgPrice = newTotalInvestment / newTotalQuantity;
              percentageChange = oldAvgPrice > 0 ? ((newAvgPrice - oldAvgPrice) / oldAvgPrice) * 100 : 0;
            }

            return (
              <Box key={token} sx={{ p: 2, borderBottom: idx < Object.keys(tokenSummaries).length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  {details?.image ? (
                    <Avatar src={details.image} sx={{ width: 32, height: 32, mr: 1.5 }} />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem' }}>{token?.charAt(0)}</Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{token?.toUpperCase()}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Invest Amount</Typography>
                  </Box>
                  <Box sx={{ width: '45%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={simulationAmounts[token] || ''}
                      onChange={(e) => handleSimulationAmountChange(token, e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 2, fontWeight: 700,
                          bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.9rem'
                        } 
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Result Pill */}
                {amount > 0 && (
                  <Box sx={{ 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: percentageChange <= 0 ? 'rgba(52, 211, 153, 0.08)' : 'rgba(248, 113, 113, 0.08)', 
                    border: `1px solid ${percentageChange <= 0 ? 'rgba(52, 211, 153, 0.2)' : 'rgba(248, 113, 113, 0.2)'}`,
                    borderRadius: 2, p: 1.5
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>New Average</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {token.toUpperCase() === 'ADA' ? newAvgPrice.toFixed(4) : newAvgPrice.toFixed(2)}€
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>Impact on Average</Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 800,
                        color: percentageChange <= 0 ? designTokens.colors.success.main : designTokens.colors.error.main 
                      }}>
                        {percentageChange > 0 ? '+' : ''}{percentageChange.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderTakeProfitsCard = () => {
    if (Object.keys(tokenSummaries).length === 0) return null;

    return (
      <Card sx={{
        mb: 2, borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        boxShadow: designTokens.shadows.sm, overflow: 'hidden'
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.05))',
          borderBottom: '1px solid rgba(236, 72, 153, 0.2)',
          px: 2, py: 2
        }}>
          <Typography variant="caption" sx={{ color: '#ec4899', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
            📈 <FormattedMessage id="takeProfits" />
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, lineHeight: 1.3 }}>
            <FormattedMessage id="takeProfitsInfo" />
          </Typography>
        </Box>

        <CardContent sx={{ p: '0 !important' }}>
          {Object.entries(tokenSummaries).map(([token, data], idx) => {
            const details = token ? tokenDetails[token.toUpperCase()] : undefined;
            const avgCost = data.quantity > 0 ? (data.investment / data.quantity) : 0;
            const currentPrice = token ? (currentPrices[token.toUpperCase()]?.price || 0) : 0;
            
            const strategy = tpStrategy[token] || 'simple';

            // --- Simple Sell Logic ---
            const simplePrice = parseFloat(tpSimpleTargetPrice[token] || '0');
            const simplePercent = tpSimpleSellPercentage[token] || 50;
            const tokensToSell = data.quantity * (simplePercent / 100);
            const simpleRevenue = tokensToSell * simplePrice;
            const simpleCostBasis = tokensToSell * avgCost;
            const simpleRealizedProfit = simpleRevenue - simpleCostBasis;
            const remainingTokens = data.quantity - tokensToSell;
            const remainingValue = remainingTokens * simplePrice;

            // --- Risk Free Logic ---
            const riskFreePercent = tpRiskFreeSellPercentage[token] || 50;
            const riskFreeTokensToSell = data.quantity * (riskFreePercent / 100);
            const priceNeeded = riskFreeTokensToSell > 0 ? (data.investment / riskFreeTokensToSell) : 0;

            // --- DCA Out Logic ---
            const dcaSteps = tpDcaSteps[token] || [{ price: '', percentage: 25 }, { price: '', percentage: 25 }];
            
            return (
              <Box key={token} sx={{ p: 2, borderBottom: idx < Object.keys(tokenSummaries).length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {details?.image ? (
                    <Avatar src={details.image} sx={{ width: 32, height: 32, mr: 1.5 }} />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem' }}>{token?.charAt(0)}</Avatar>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{token?.toUpperCase()}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      <FormattedMessage id="averageCost" />: {token.toUpperCase() === 'ADA' ? avgCost.toFixed(4) : avgCost.toFixed(2)}€
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, bgcolor: 'rgba(255,255,255,0.05)', px: 1, py: 0.5, borderRadius: 1 }}>
                    Total: {data.investment.toFixed(2)}€
                  </Typography>
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
                  <Chip 
                    label={<FormattedMessage id="simpleSell" />} 
                    onClick={() => setTpStrategy(p => ({ ...p, [token]: 'simple' }))}
                    sx={{ flexShrink: 0, bgcolor: strategy === 'simple' ? '#ec4899' : 'rgba(255,255,255,0.05)', color: strategy === 'simple' ? '#fff' : 'inherit', fontWeight: strategy === 'simple' ? 700 : 500 }}
                  />
                  <Chip 
                    label={<FormattedMessage id="riskFree" />} 
                    onClick={() => setTpStrategy(p => ({ ...p, [token]: 'riskFree' }))}
                    sx={{ flexShrink: 0, bgcolor: strategy === 'riskFree' ? '#ec4899' : 'rgba(255,255,255,0.05)', color: strategy === 'riskFree' ? '#fff' : 'inherit', fontWeight: strategy === 'riskFree' ? 700 : 500 }}
                  />
                  <Chip 
                    label={<FormattedMessage id="dcaOut" />} 
                    onClick={() => setTpStrategy(p => ({ ...p, [token]: 'dca' }))}
                    sx={{ flexShrink: 0, bgcolor: strategy === 'dca' ? '#ec4899' : 'rgba(255,255,255,0.05)', color: strategy === 'dca' ? '#fff' : 'inherit', fontWeight: strategy === 'dca' ? 700 : 500 }}
                  />
                </Box>

                {strategy === 'simple' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth size="small" type="number" inputMode="decimal"
                        label={intl.formatMessage({ id: 'targetPrice' })}
                        value={tpSimpleTargetPrice[token] || ''}
                        onChange={(e) => setTpSimpleTargetPrice(prev => ({ ...prev, [token]: e.target.value.replace(',', '.') }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      />
                      <TextField
                        fullWidth size="small" type="number" inputMode="decimal"
                        label={intl.formatMessage({ id: 'sellPercentage' })}
                        value={tpSimpleSellPercentage[token] || ''}
                        onChange={(e) => setTpSimpleSellPercentage(prev => ({ ...prev, [token]: Number(e.target.value) }))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Box>
                    <Box sx={{ px: 1 }}>
                      <Slider 
                        value={simplePercent} 
                        onChange={(_, v) => setTpSimpleSellPercentage(prev => ({ ...prev, [token]: v as number }))} 
                        step={5} marks min={0} max={100} valueLabelDisplay="auto" 
                        sx={{ color: '#ec4899' }}
                      />
                    </Box>

                    {simplePrice > 0 && simplePercent > 0 && (
                      <Box sx={{ 
                        display: 'flex', flexDirection: 'column', gap: 0.5,
                        background: 'rgba(255,255,255,0.03)', borderRadius: 2, p: 1.5
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}><FormattedMessage id="revenue" /></Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>{simpleRevenue.toFixed(2)}€</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}><FormattedMessage id="realizedProfit" /></Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: simpleRealizedProfit >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main }}>
                            {simpleRealizedProfit >= 0 ? '+' : ''}{simpleRealizedProfit.toFixed(2)}€
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}><FormattedMessage id="remaining" /> ({remainingTokens.toFixed(2)})</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: designTokens.colors.info.main }}>{remainingValue.toFixed(2)}€</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {strategy === 'riskFree' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.4 }}>
                      <FormattedMessage id="recoverInvestmentInfo" /> <strong>{riskFreePercent}%</strong>.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>100% ROI</Typography>
                      <Box sx={{ flex: 1, px: 1 }}>
                        <Slider 
                          value={riskFreePercent} 
                          onChange={(_, v) => setTpRiskFreeSellPercentage(prev => ({ ...prev, [token]: v as number }))} 
                          step={5} marks min={5} max={100} valueLabelDisplay="auto" 
                          sx={{ color: '#ec4899' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{riskFreePercent}%</Typography>
                    </Box>

                    {priceNeeded > 0 && (
                      <Box sx={{ 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: 'rgba(52, 211, 153, 0.08)', border: '1px solid rgba(52, 211, 153, 0.2)',
                        borderRadius: 2, p: 1.5
                      }}>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}><FormattedMessage id="priceNeeded" /></Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: designTokens.colors.success.main }}>
                          {token.toUpperCase() === 'ADA' ? priceNeeded.toFixed(4) : priceNeeded.toFixed(2)}€
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {strategy === 'dca' && (() => {
                  let totalPercentage = 0;
                  let totalTokensSold = 0;
                  let totalRevenue = 0;

                  dcaSteps.forEach(step => {
                    const stepPrice = parseFloat(step.price || '0');
                    const stepTokens = data.quantity * (step.percentage / 100);
                    totalPercentage += step.percentage;
                    totalTokensSold += stepTokens;
                    totalRevenue += stepPrice * stepTokens;
                  });

                  const totalProfit = totalRevenue - (totalTokensSold * avgCost);
                  const remainingTokens = data.quantity - totalTokensSold;
                  const isOverSold = totalPercentage > 100;

                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {dcaSteps.map((step, sIdx) => {
                        const stepPrice = parseFloat(step.price || '0');
                        const stepTokens = data.quantity * (step.percentage / 100);
                        const stepRevenue = stepPrice * stepTokens;
                        const stepProfit = stepRevenue - (stepTokens * avgCost);

                        return (
                          <Box key={sIdx} sx={{ background: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}><FormattedMessage id="step" /> {sIdx + 1}</Typography>
                              {dcaSteps.length > 1 && (
                                <IconButton size="small" sx={{ p: 0 }} onClick={() => {
                                  const newSteps = [...dcaSteps];
                                  newSteps.splice(sIdx, 1);
                                  setTpDcaSteps(p => ({ ...p, [token]: newSteps }));
                                }}>
                                  <Delete sx={{ fontSize: '1rem', color: designTokens.colors.error.main }} />
                                </IconButton>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                fullWidth size="small" type="number" inputMode="decimal"
                                placeholder={intl.formatMessage({ id: 'targetPrice' })}
                                value={step.price}
                                onChange={(e) => {
                                  const newSteps = [...dcaSteps];
                                  newSteps[sIdx].price = e.target.value.replace(',', '.');
                                  setTpDcaSteps(p => ({ ...p, [token]: newSteps }));
                                }}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                              />
                              <TextField
                                sx={{ width: 100 }} size="small" type="number" inputMode="decimal"
                                placeholder="%"
                                value={step.percentage || ''}
                                onChange={(e) => {
                                  const newSteps = [...dcaSteps];
                                  newSteps[sIdx].percentage = Number(e.target.value);
                                  setTpDcaSteps(p => ({ ...p, [token]: newSteps }));
                                }}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              />
                            </Box>
                            {stepPrice > 0 && step.percentage > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  <FormattedMessage id="revenue" />: {stepRevenue.toFixed(2)}€
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: stepProfit >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main }}>
                                  {stepProfit >= 0 ? '+' : ''}{stepProfit.toFixed(2)}€
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => setTpDcaSteps(p => ({ ...p, [token]: [...(tpDcaSteps[token] || [{price: '', percentage: 25}, {price: '', percentage: 25}]), { price: '', percentage: 25 }] }))}
                        sx={{ borderColor: '#ec4899', color: '#ec4899', borderRadius: 2 }}
                        disabled={isOverSold}
                      >
                        + <FormattedMessage id="addStep" />
                      </Button>

                      {totalRevenue > 0 && (
                        <Box sx={{ 
                          display: 'flex', flexDirection: 'column', gap: 0.5,
                          background: isOverSold ? 'rgba(248, 113, 113, 0.1)' : 'rgba(255,255,255,0.03)', 
                          border: isOverSold ? `1px solid ${designTokens.colors.error.main}` : 'none',
                          borderRadius: 2, p: 1.5, mt: 1
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, mb: 0.5, color: '#ec4899' }}>
                            Total DCA Out
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}><FormattedMessage id="revenue" /></Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{totalRevenue.toFixed(2)}€</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}><FormattedMessage id="realizedProfit" /></Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: totalProfit >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main }}>
                              {totalProfit >= 0 ? '+' : ''}{totalProfit.toFixed(2)}€
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, color: isOverSold ? designTokens.colors.error.main : 'inherit' }}>
                              <FormattedMessage id="remaining" /> ({totalPercentage}%)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: isOverSold ? designTokens.colors.error.main : designTokens.colors.info.main }}>
                              {remainingTokens.toFixed(2)} Tokens
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const uniqueTokens = Array.from(new Set(investments.map(inv => inv.token)));

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: 'auto',
      pb: 10,
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* ── Hero Portfolio Card ── */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box sx={{
          m: 2,
          p: 2.5,
          borderRadius: designTokens.borderRadius.xl,
          background: designTokens.colors.primary.gradient,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: designTokens.shadows.lg,
        }}>
          {/* Decorative circles */}
          <Box sx={{
            position: 'absolute', top: -30, right: -30, width: 120, height: 120,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -40, left: -20, width: 100, height: 100,
            borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
          }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase' }}>
              <FormattedMessage id="portfolioSummary" />
            </Typography>
            <IconButton
              size="small"
              onClick={handleRefresh}
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
            >
              {isRefreshing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh fontSize="small" />}
            </IconButton>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: -0.5 }}>
            €{Math.floor(portfolioSummary.currentValue).toLocaleString()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {portfolioSummary.profitLoss >= 0 ? (
              <TrendingUp fontSize="small" sx={{ color: designTokens.colors.success.main }} />
            ) : (
              <TrendingDown fontSize="small" sx={{ color: designTokens.colors.error.main }} />
            )}
            <Typography variant="body2" sx={{
              color: portfolioSummary.profitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
              fontWeight: 700,
            }}>
              {portfolioSummary.profitLoss >= 0 ? '+' : ''}€{portfolioSummary.profitLoss.toFixed(2)}
              {' '}({portfolioSummary.profitLossPercentage.toFixed(2)}%)
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.15)' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                <FormattedMessage id="totalInvestment" />
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                €{Math.floor(portfolioSummary.totalInvestment).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                <FormattedMessage id="profitLoss" />
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: 700,
                color: portfolioSummary.profitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
              }}>
                {portfolioSummary.profitLossPercentage.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* ── Token Price Chips (Horizontal Scroll) ── */}
      <Box sx={{
        display: 'flex',
        gap: 1,
        px: 2,
        pb: 2,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}>
        {uniqueTokens.map((token, idx) => {
          const priceData = token ? currentPrices[token.toUpperCase()] : undefined;
          const details = token ? tokenDetails[token.toUpperCase()] : undefined;
          const isPositive = priceData && priceData.price_change_percentage_24h >= 0;

          return (
            <motion.div
              key={token}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Chip
                avatar={
                  details?.image ? (
                    <Avatar src={details.image} sx={{ width: 22, height: 22 }} />
                  ) : (
                    <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem' }}>{token?.charAt(0)}</Avatar>
                  )
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                      {token?.toUpperCase()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                        {formatPrice(token, priceData)}
                      </Typography>
                      {priceData && priceData.price_change_percentage_24h !== undefined && (
                        <Typography variant="caption" sx={{
                          fontSize: '0.55rem',
                          color: isPositive ? designTokens.colors.success.main : designTokens.colors.error.main,
                          fontWeight: 600,
                        }}>
                          {isPositive ? '+' : ''}{priceData.price_change_percentage_24h.toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  </Box>
                }
                onClick={() => toggleToken(token)}
                sx={{
                  height: 'auto',
                  py: 0.75,
                  px: 0.5,
                  borderRadius: designTokens.borderRadius.lg,
                  background: expandedTokens.has(token)
                    ? designTokens.colors.primary.gradient
                    : designTokens.colors.background.paper,
                  border: expandedTokens.has(token)
                    ? 'none'
                    : designTokens.glassmorphism.border,
                  color: expandedTokens.has(token) ? '#fff' : 'inherit',
                  boxShadow: expandedTokens.has(token) ? designTokens.shadows.md : designTokens.shadows.sm,
                  transition: `all ${designTokens.transitions.fast}`,
                  '& .MuiChip-label': { px: 0.75 },
                  flexShrink: 0,
                }}
              />
            </motion.div>
          );
        })}
      </Box>

      {/* ── Token Cards ── */}
      <Box sx={{ px: 2 }}>
        <AnimatePresence>
          {uniqueTokens.map((token, idx) => {
            const priceData = token ? currentPrices[token.toUpperCase()] : undefined;
            const details = token ? tokenDetails[token.toUpperCase()] : undefined;
            const summary = tokenSummaries[token];
            if (!summary) return null;

            const currentPrice = priceData?.price || 0;
            const currentValue = Math.floor(summary.quantity * currentPrice);
            const profitLoss = Math.floor(currentValue - summary.investment);
            const profitLossPercentage = summary.investment > 0 ? (profitLoss / summary.investment) * 100 : 0;
            const avgPrice = summary.investment / summary.quantity;
            const isExpanded = expandedTokens.has(token);
            const isLongTerm = longTermTokens.has(token);
            const tokenInvestments = investments
              .filter(inv => inv.token === token)
              .sort((a, b) => {
                const dateA = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0;
                const dateB = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0;
                return dateA - dateB;
              });

            return (
              <motion.div
                key={token}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06 }}
                layout
              >
                <Card sx={{
                  mb: 1.5,
                  borderRadius: designTokens.borderRadius.lg,
                  background: designTokens.colors.background.paper,
                  border: isLongTerm
                    ? `1px solid ${designTokens.colors.success.main}40`
                    : designTokens.glassmorphism.border,
                  boxShadow: designTokens.shadows.sm,
                  overflow: 'hidden',
                  transition: `all ${designTokens.transitions.fast}`,
                }}>
                  {/* Card Header */}
                  <Box
                    onClick={() => toggleToken(token)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      cursor: 'pointer',
                      '&:active': { opacity: 0.8 },
                    }}
                  >
                    {/* Token Logo */}
                    {details?.image ? (
                      <Avatar src={details.image} sx={{ width: 36, height: 36, mr: 1.5 }} />
                    ) : (
                      <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: designTokens.colors.primary.main, fontSize: '0.9rem' }}>
                        {token?.charAt(0)}
                      </Avatar>
                    )}

                    {/* Token Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {token?.toUpperCase()}
                        </Typography>
                        {isLongTerm && (
                          <Star sx={{ fontSize: '0.9rem', color: designTokens.colors.warning.main }} />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary }}>
                        {formatQuantity(token, summary.quantity)} tokens · avg {formatPurchasePrice(token, avgPrice)}€
                      </Typography>
                    </Box>

                    {/* Value + P/L */}
                    <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {currentValue.toLocaleString()}€
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        color: profitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                      }}>
                        {profitLoss >= 0 ? <ArrowDropUp sx={{ fontSize: '1rem' }} /> : <ArrowDropDown sx={{ fontSize: '1rem' }} />}
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {profitLoss >= 0 ? '+' : ''}{profitLoss}€ ({profitLossPercentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>

                    {/* Expand Icon */}
                    <IconButton size="small" sx={{ ml: 0.5, color: designTokens.colors.text.secondary }}>
                      {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </Box>

                  {/* Expanded Content — Individual Purchases */}
                  <Collapse in={isExpanded} timeout="auto">
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    <Box sx={{ px: 1.5, py: 1 }}>
                      {tokenInvestments.map((inv, i) => {
                        const invCurrentValue = Math.floor(currentPrice * Number(inv.quantity));
                        const invProfitLoss = Math.floor(invCurrentValue - parseFloat(inv.investment));
                        const isOldEnough = inv.purchaseDate &&
                          (new Date().getTime() - new Date(inv.purchaseDate).getTime()) / (1000 * 3600 * 24) > 365;

                        return (
                          <motion.div
                            key={inv.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04 }}
                          >
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 1,
                              borderBottom: i < tokenInvestments.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                            }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {Math.floor(parseFloat(inv.investment))}€ → {formatQuantity(token, Number(inv.quantity))}
                                  </Typography>
                                  {isOldEnough && (
                                    <Star sx={{ fontSize: '0.7rem', color: designTokens.colors.warning.main }} />
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, fontSize: '0.65rem' }}>
                                  {inv.purchaseDate ? new Date(inv.purchaseDate).toLocaleDateString() : '-'}
                                  {' · @ '}{formatPurchasePrice(token, inv.purchasePrice)}€
                                </Typography>
                              </Box>

                              <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {invCurrentValue}€
                                </Typography>
                                <Typography variant="caption" sx={{
                                  display: 'block',
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  color: invProfitLoss >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main,
                                }}>
                                  {invProfitLoss >= 0 ? '+' : ''}{invProfitLoss}€
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 0 }}>
                                <IconButton size="small" onClick={() => handleEdit(inv)} sx={{ p: 0.5 }}>
                                  <Edit sx={{ fontSize: '0.9rem', color: designTokens.colors.text.secondary }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(inv.id)} sx={{ p: 0.5 }}>
                                  <Delete sx={{ fontSize: '0.9rem', color: designTokens.colors.error.light }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </motion.div>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>

      {/* ── Simulation Cards & Analysis Tabs ── */}
      <Box sx={{ px: 2, pb: 4, width: '100%', boxSizing: 'border-box' }}>
        <Tabs 
          value={activeAnalysisTab} 
          onChange={(_, newValue) => setActiveAnalysisTab(newValue)} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            mb: 2, 
            minHeight: 40,
            '& .MuiTabs-indicator': { backgroundColor: designTokens.colors.primary.main },
            '& .MuiTab-root': { 
              textTransform: 'none', 
              fontWeight: 600, 
              minHeight: 40,
              fontSize: '0.85rem',
              color: 'rgba(255,255,255,0.6)',
              '&.Mui-selected': { color: '#fff' }
            } 
          }}
        >
          <Tab label={<FormattedMessage id="taxExemptTokens" />} />
          <Tab label={<FormattedMessage id="forecastSimulation" />} />
          <Tab label={<FormattedMessage id="investmentSimulation" />} />
          <Tab label={<FormattedMessage id="takeProfits" />} />
        </Tabs>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeAnalysisTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeAnalysisTab === 0 && renderTaxExemptCard()}
            {activeAnalysisTab === 1 && renderForecastSimulationCard()}
            {activeAnalysisTab === 2 && renderInvestmentSimulationCard()}
            {activeAnalysisTab === 3 && renderTakeProfitsCard()}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* ── Add FAB ── */}
      <Fab
        color="primary"
        onClick={() => { setEditingInvestment(undefined); setIsFormOpen(true); }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: designTokens.colors.primary.gradient,
          boxShadow: designTokens.shadows.lg,
          '&:hover': { boxShadow: designTokens.shadows.glow },
        }}
      >
        <AddIcon />
      </Fab>

      {/* ── Add Dialog ── */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} fullScreen>
        <DialogTitle sx={{
          background: designTokens.colors.primary.gradient,
          color: '#fff',
          fontWeight: 700,
        }}>
          <FormattedMessage id="addInvestment" />
        </DialogTitle>
        <DialogContent sx={{ pt: 3, background: designTokens.colors.background.default }}>
          <InvestmentForm
            onAddInvestment={handleAddInvestment}
            onUpdateInvestment={handleUpdateInvestment}
            userId={user.uid}
            onCancel={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={isEditFormOpen} onClose={() => { setIsEditFormOpen(false); setEditingInvestment(undefined); }} fullScreen>
        <DialogTitle sx={{
          background: designTokens.colors.primary.gradient,
          color: '#fff',
          fontWeight: 700,
        }}>
          <FormattedMessage id="editInvestment" />
        </DialogTitle>
        <DialogContent sx={{ pt: 3, background: designTokens.colors.background.default }}>
          <InvestmentForm
            onAddInvestment={handleAddInvestment}
            onUpdateInvestment={handleUpdateInvestment}
            investment={editingInvestment}
            userId={user.uid}
            onCancel={() => { setIsEditFormOpen(false); setEditingInvestment(undefined); }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MobileInvestments;
